// Google Analytics 4 event tracking utility

declare global {
  interface Window {
    gtag?: (
      command: string,
      eventName: string,
      params?: Record<string, any>
    ) => void;
  }
}

interface EventParams {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

/**
 * Track a custom event in Google Analytics 4
 * @param eventName - Name of the event (e.g., 'cta_click', 'form_submit')
 * @param params - Additional parameters for the event
 */
export const trackEvent = (eventName: string, params?: EventParams) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      site_version: 'lovable',
      ...params,
    });
  }
};

/**
 * Track page views for SPA navigation
 */
export const trackPageView = (path: string, title: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};

/**
 * Track errors
 */
export const trackError = (error: Error, context?: string) => {
  trackEvent('exception', {
    description: error.message,
    context: context,
    fatal: false,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent('cta_click', {
    category: 'engagement',
    label: ctaName,
    location: location,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (formName: string, success: boolean = true) => {
  trackEvent('form_submit', {
    category: 'conversion',
    label: formName,
    success: success,
  });
};

/**
 * Track navigation clicks
 */
export const trackNavigation = (linkName: string, destination: string) => {
  trackEvent('navigation_click', {
    category: 'navigation',
    label: linkName,
    destination: destination,
  });
};

/**
 * Track feature interactions
 */
export const trackFeatureInteraction = (featureName: string, action: string) => {
  trackEvent('feature_interaction', {
    category: 'feature',
    label: featureName,
    action: action,
  });
};

/**
 * Track video plays
 */
export const trackVideoPlay = (videoName: string, location: string) => {
  trackEvent('video_play', {
    category: 'engagement',
    label: videoName,
    location: location,
  });
};

/**
 * Track search queries
 */
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', {
    category: 'engagement',
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

/**
 * Track pricing calculator usage
 */
export const trackPricingCalculator = (action: string, values?: Record<string, any>) => {
  trackEvent('pricing_calculator', {
    category: 'tool',
    action: action,
    ...values,
  });
};

/**
 * Track roadmap votes
 */
export const trackRoadmapVote = (itemTitle: string, voteType: 'upvote' | 'downvote') => {
  trackEvent('roadmap_vote', {
    category: 'engagement',
    item: itemTitle,
    vote_type: voteType,
  });
};

/**
 * Track integration page views
 */
export const trackIntegrationView = (integrationName: string) => {
  trackEvent('integration_view', {
    category: 'product',
    integration: integrationName,
  });
};
